/**
 * StateLabel — the "Qoryn is listening." text below the halo.
 *
 * Consumes the SAME `presentation` value the QorynHalo renders from,
 * derived by `derivePresentation` in `@/lib/haloPresentation`. The two
 * can never disagree.
 */

import { motion, AnimatePresence } from "framer-motion";

const PHRASE_BY_PRESENTATION = {
    idle: "is ready.",
    connecting: "is connecting…",
    text_only: "is in text mode.",
    // User-facing phrase for the internal `held` presentation. The
    // underlying state name is unchanged (operator-facing diagnostics
    // still surface the scoped hold description); the label copy
    // reads plain "is paused." to the founder.
    held: "is paused.",
    listening: "is listening.",
    muted: "is on standby.",
    thinking: "is thinking.",
    speaking: "is speaking.",
    speaking_muted: "is speaking (output muted).",
    error: "encountered an issue.",
};

// Backwards-compatible fallback for callers that haven't been migrated
// off raw agentState yet. Does NOT fold mic/output-mute in — that truth
// lives in `presentation`.
const AGENT_TO_PRESENTATION = {
    idle: "idle",
    connecting: "connecting",
    listening: "listening",
    thinking: "thinking",
    speaking: "speaking",
    error: "error",
};

const ACTIVE_PRESENTATIONS = new Set([
    "listening",
    "thinking",
    "speaking",
    "speaking_muted",
]);

export function StateLabel({ agentState = "idle", presentation = null }) {
    const p =
        presentation && PHRASE_BY_PRESENTATION[presentation]
            ? presentation
            : (AGENT_TO_PRESENTATION[agentState] ?? "idle");
    const phrase = PHRASE_BY_PRESENTATION[p] ?? "is ready.";
    const isError = p === "error";
    const isHeld = p === "held";
    return (
        <div
            data-testid="state-label"
            data-agent-state={agentState}
            data-presentation={p}
            className="flex flex-col items-center gap-3"
        >
            <AnimatePresence mode="wait">
                <motion.div
                    key={p}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="font-display text-2xl sm:text-[26px] font-light tracking-tight text-center"
                >
                    <span className="text-qoryn-text">Qoryn</span>{" "}
                    <span
                        className={
                            isError
                                ? "text-red-400"
                                : isHeld
                                  ? "text-qoryn-textDim"
                                  : "text-qoryn-cyan"
                        }
                        data-testid="state-label-phrase"
                    >
                        {phrase}
                    </span>
                </motion.div>
            </AnimatePresence>
            <div
                className="flex items-center gap-1"
                aria-hidden="true"
                data-testid="state-progress"
            >
                {Array.from({ length: 24 }).map((_, i) => (
                    <span
                        key={i}
                        className={`h-[2px] w-[2px] rounded-full ${
                            ACTIVE_PRESENTATIONS.has(p)
                                ? "bg-qoryn-cyan/60"
                                : "bg-white/10"
                        }`}
                    />
                ))}
            </div>
        </div>
    );
}

export default StateLabel;
